import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Theme } from '../types';
import config from '../config';
import { MessageWithCitations } from './chat/chatTypes';
import {
  FileMeta,
  FileFamily,
  FileLibrary,
  EMPTY_LIBRARY,
  listLibrary,
  uploadFile,
  deleteFile,
  reprocessFile,
  getDownloadUrl,
  setFileSelection,
  readPendingFileSelection,
  writePendingFileSelection,
  notifyFilesChanged,
  FILES_CHANGED_EVENT,
  patchFile,
  createFolder,
  deleteFolder,
  prettySize,
  fileFamily,
  childFolders,
  filesInFolder,
  joinFolder,
  parentFolder,
  folderDepth,
  FileWorkspaceError,
  UnauthorizedError,
} from './chat/fileWorkspaceService';
import { FileWorkspaceDesk } from './chat/FileWorkspaceDesk';
import {
  inspectFromMessages,
  localizeStepLabel,
  OPEN_INSPECTOR_EVENT,
  InspectorSource,
  groupInspectorSources,
  faviconUrl,
} from './chat/agentTrace';

// Obsidian error signal, the one hardcoded colour the workspace panel is allowed
// (per spec: #E5484D at 80%). Everything else pulls from theme tokens so both
// light and dark render correctly.
const ERROR_RED = 'rgba(229,71,77,0.8)';

// Permanent right column (desktop only; the caller gates rendering on
// !isMobile). Width must stay in sync with the 320px reservations in
// useTheme.ts (mainContent marginRight), MainContent.tsx and ChatInput.tsx
// (composer right offset), and Chat.tsx (scroll-to-bottom offset).
export const RIGHT_PANEL_WIDTH = 320;

interface RightSidebarProps {
  theme: Theme;
  themeColors: any; // Add proper type from your theme definitions
  currentConversationId: number | null;
  // Project this chat is filed under (or the pending project on a new chat).
  // AUTO always reads that project's files; the panel must show them too.
  projectId?: number | null;
  // Read-only: the loaded conversation messages. Used purely client-side to
  // derive the "Hasil" section (AI-generated decks / images). No backend calls.
  messages?: MessageWithCitations[];
  // Narrow windows (<1100px): the panel FLOATS above the thread instead of
  // docking, so the centre column keeps its width. Needs a solid surface +
  // shadow, since the docked look is transparent-over-app-background.
  overlay?: boolean;
}

// ---------------------------------------------------------------------------
// Conversation workspace panel (Obsidian). Shows everything the conversation
// "has": uploads (File) plus AI-generated outputs derived from the messages
// (Hasil). The whole panel is the file drop target.
// ---------------------------------------------------------------------------

// Compact 16px type glyph, drawn per mime family, stroked in whatever colour
// (ink3) the caller passes.
const FileTypeIcon: React.FC<{ family: FileFamily; color: string }> = ({ family, color }) => {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: color,
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (family) {
    case 'img':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      );
    case 'pdf':
      return (
        <svg {...common}>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <path d="M14 3v6h6" />
          <path d="M8.5 13H10a1.25 1.25 0 0 1 0 2.5H8.5zM8.5 13v5" />
        </svg>
      );
    case 'sheet':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
        </svg>
      );
    case 'zip':
      return (
        <svg {...common}>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <path d="M14 3v6h6" />
          <path d="M12 11v1M12 14v1M12 17v1" />
        </svg>
      );
    case 'doc':
      return (
        <svg {...common}>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <path d="M14 3v6h6" />
          <path d="M8 13h8M8 17h5" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <path d="M14 3v6h6" />
        </svg>
      );
  }
};

// 16px presentation glyph for deck rows in the Hasil section.
const SlidesIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="12" rx="1.5" />
    <path d="M12 16v3M9 21h6" />
    <path d="M7 9.5h7" />
  </svg>
);

// Tiny external-link arrow (top-right glyph on deck rows / thumbnails).
const ArrowUpRight: React.FC<{ color: string }> = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 17L17 7" />
    <path d="M8 7h9v9" />
  </svg>
);

const DownloadIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" />
    <path d="M7 10l5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);

const TrashIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M6 6l1 14h10l1-14" />
  </svg>
);

// Reuses the global `@keyframes spin` from index.css.
const Spinner: React.FC<{ color: string; size?: number }> = ({ color, size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
    <path d="M12 3a9 9 0 1 0 9 9" />
  </svg>
);

// Check glyph drawn inside a selected (jade-filled) selection control.
const CheckGlyph: React.FC<{ color: string }> = ({ color }) => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// 16px round selection control on a file row's left edge. Selected = jade fill +
// white check; unselected = 1.5px line2 ring that turns jade on hover; busy = a
// tiny spinner while a toggle is in flight. Only rendered when a conversation is
// active (there is nothing to select a file "into" otherwise).
const SelectionControl: React.FC<{
  selected: boolean;
  busy: boolean;
  c: any;
  onToggle: () => void;
}> = ({ selected, busy, c, onToggle }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-label={selected ? 'Nonaktifkan dari percakapan' : 'Aktifkan di percakapan'}
      title={selected ? 'Dipakai di percakapan ini' : 'Aktifkan untuk percakapan ini'}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        flexShrink: 0,
        width: '16px',
        height: '16px',
        borderRadius: '999px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        lineHeight: 0,
        background: selected ? c.jade : 'transparent',
        border: selected ? '1.5px solid transparent' : `1.5px solid ${hover ? c.jade : c.line2}`,
        transition: 'background 0.12s ease, border-color 0.12s ease',
      }}
    >
      {busy ? (
        <Spinner color={selected ? '#FFFFFF' : c.ink3} size={10} />
      ) : selected ? (
        <CheckGlyph color="#FFFFFF" />
      ) : null}
    </button>
  );
};

// Hover-reveal 12px action button (download / delete).
const ConfirmDeleteActions: React.FC<{
  danger: string;
  muted: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ danger, muted, onConfirm, onCancel }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
    <button
      type="button"
      autoFocus
      onClick={onConfirm}
      title="Hapus. Tindakan ini tidak bisa dibatalkan."
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 600,
        color: danger,
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
      }}
    >
      Yakin hapus?
    </button>
    <button
      type="button"
      onClick={onCancel}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        fontSize: '12px',
        color: muted,
        fontFamily: 'inherit',
      }}
    >
      Batal
    </button>
  </div>
);

const ListAction: React.FC<{
  label: string;
  color: string;
  line: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, color, line, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      height: '30px',
      padding: '0 10px',
      borderRadius: '8px',
      background: 'none',
      border: `1px dashed ${line}`,
      color,
      fontSize: '12.5px',
      cursor: 'pointer',
      fontFamily: 'inherit',
    }}
  >
    {children}
    {label}
  </button>
);

const UploadGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 16V5" />
    <path d="m8 8 4-4 4 4" />
    <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </svg>
);

const FolderPlusGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    <path d="M12 11v6" />
    <path d="M9 14h6" />
  </svg>
);

const IconButton: React.FC<{
  label: string;
  color: string;
  hoverColor: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, color, hoverColor, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    onMouseEnter={(e) => { e.currentTarget.style.color = hoverColor; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = color; }}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      borderRadius: '6px',
      color,
      lineHeight: 0,
    }}
  >
    {children}
  </button>
);

// Uppercase, letterspaced section label (File / Hasil). Optional one-line hint
// under the label, sentence case, so Aktif vs Library vs Project is readable.
const SectionLabel: React.FC<{ color: string; hint?: string; children: React.ReactNode }> = ({
  color,
  hint,
  children,
}) => (
  <div style={{ marginBottom: hint ? '10px' : '11px' }}>
    <div
      style={{
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color,
      }}
    >
      {children}
    </div>
    {hint && (
      <div
        style={{
          fontSize: '11px',
          fontWeight: 400,
          letterSpacing: 0,
          textTransform: 'none',
          color,
          marginTop: '4px',
          lineHeight: 1.4,
        }}
      >
        {hint}
      </div>
    )}
  </div>
);

// Composed empty-state illustration: three overlapping rounded file cards drawn
// in hairline strokes, with a single jade accent dot that brightens while a file
// is being dragged over the panel. Pure theme tokens, so both themes render.
const WorkspaceIllustration: React.FC<{ c: any; active: boolean }> = ({ c, active }) => (
  <svg width="96" height="78" viewBox="0 0 96 78" fill="none" aria-hidden="true">
    {/* Left card, fanned behind */}
    <g transform="rotate(-13 34 40)">
      <rect x="22" y="17" width="30" height="44" rx="6" fill={c.panel} stroke={c.line2} strokeWidth="1.4" />
    </g>
    {/* Right card, fanned behind */}
    <g transform="rotate(13 62 40)">
      <rect x="44" y="17" width="30" height="44" rx="6" fill={c.panel} stroke={c.line2} strokeWidth="1.4" />
    </g>
    {/* Front card (upright) with content lines */}
    <rect x="33" y="15" width="30" height="46" rx="6" fill={c.panel2} stroke={c.line2} strokeWidth="1.4" />
    <path
      d="M39 27h18M39 34h18M39 41h11"
      stroke={c.line2}
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    {/* Jade accent dot on the front card corner (halo appears while dragging) */}
    <circle
      cx="60"
      cy="53"
      r="9"
      fill={c.jade}
      style={{ opacity: active ? 0.16 : 0, transition: 'opacity 0.2s ease' }}
    />
    <circle
      cx="60"
      cy="53"
      r="4.5"
      fill={c.jade}
      style={{ opacity: active ? 1 : 0.55, transition: 'opacity 0.2s ease' }}
    />
  </svg>
);

// Muted file-type pill for the initial (no-conversation) empty state.
const TypePill: React.FC<{ c: any; label: string }> = ({ c, label }) => (
  <span
    style={{
      fontSize: '10.5px',
      color: c.ink3,
      background: c.panel,
      border: `1px solid ${c.line}`,
      borderRadius: '999px',
      padding: '3px 9px',
      lineHeight: 1,
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </span>
);

interface PendingUpload {
  tempId: string;
  filename: string;
  size: number;
}

// ---------------------------------------------------------------------------
// File workspace state + IO. Lifted out of the old FilesSection so the whole
// panel (not a boxed dashed zone) can be the drop target and the header badge /
// empty-state logic can read the file count. The upload / poll / delete /
// download behaviour is unchanged.
// ---------------------------------------------------------------------------
function useFileWorkspace(conversationId: number | null, projectId: number | null) {
  const [files, setFiles] = useState<FileMeta[]>([]);
  const [projectFiles, setProjectFiles] = useState<FileMeta[]>([]);
  const [libraryMeta, setLibraryMeta] = useState<FileLibrary>(EMPTY_LIBRARY);
  const [projectMeta, setProjectMeta] = useState<FileLibrary>(EMPTY_LIBRARY);
  // Pilihan file untuk CHAT BARU (belum ada conversation_id): disimpan lokal,
  // diterapkan oleh useMessageSubmit ke percakapan yang dibuat pesan pertama,
  // lalu dibersihkan. Kunci localStorage dibaca ulang di event files-changed.
  const [pendingIds, setPendingIds] = useState<number[]>(() => readPendingFileSelection());
  const updatePending = useCallback((ids: number[]) => {
    setPendingIds(ids);
    writePendingFileSelection(ids);
    notifyFilesChanged();
  }, []);
  const [uploading, setUploading] = useState<PendingUpload[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Ids of files whose selection toggle is mid-flight (drives the control spinner).
  const [togglingIds, setTogglingIds] = useState<number[]>([]);

  // 401 -> localStorage already cleared by the service; just bounce to /login.
  const handleAuthError = useCallback((e: unknown): boolean => {
    if (e instanceof UnauthorizedError) {
      window.location.href = '/login';
      return true;
    }
    return false;
  }, []);

  // The server's `detail` is NEVER rendered raw. Most of it is our own
  // Indonesian cap copy (allowlisted below and passed through verbatim), but
  // the ownership 403 answers in English AND reads as factually wrong to
  // someone legitimately viewing a shared conversation. Everything outside the
  // allowlist is replaced by status-mapped Indonesian copy.
  const reportError = useCallback((e: unknown) => {
    if (handleAuthError(e)) return;
    if (e instanceof FileWorkspaceError) {
      const KNOWN_ID_DETAILS = [
        'File maksimal 15MB',
        'Maksimal 20 file aktif per percakapan',
        'Maksimal 200 file di library',
        'Maksimal 200 file di project',
        'Kuota penyimpanan 100MB penuh',
        'File tidak ditemukan',
        'Hanya pengunggah atau pemilik tim yang bisa menghapus file ini',
        'Hanya pengunggah atau pemilik tim yang bisa mengubah file ini',
        'Nama folder tidak valid',
        'Folder maksimal 3 tingkat',
        'Folder sudah ada',
        'Folder masih berisi file',
        'Folder tidak ditemukan',
        'Nama file tidak valid',
      ];
      const detail = (e.detail || '').trim();
      if (KNOWN_ID_DETAILS.includes(detail)) {
        setError(detail);
        return;
      }
      if (e.status === 403) {
        setError('File di percakapan ini hanya bisa dikelola pemiliknya.');
      } else if (e.status === 413) {
        setError('File terlalu besar. Maksimal 15 MB per file.');
      } else if (e.status === 404) {
        setError('File tidak ditemukan. Coba segarkan halaman.');
      } else if (e.status >= 500) {
        setError('Server sedang bermasalah. Coba lagi sebentar.');
      } else {
        setError('Terjadi kesalahan. Coba lagi.');
      }
      return;
    }
    setError('Terjadi kesalahan. Coba lagi.');
  }, [handleAuthError]);

  const refreshLists = useCallback(async () => {
    const [library, project] = await Promise.all([
      listLibrary(conversationId),
      projectId != null ? listLibrary(null, projectId) : Promise.resolve(EMPTY_LIBRARY),
    ]);
    return { library, project };
  }, [conversationId, projectId]);

  // Load (and reset) whenever the conversation or project changes. The whole
  // personal library is listed in every case; project files are a separate
  // always-on group when this chat is filed under a project.
  useEffect(() => {
    setError(null);
    setUploading([]);
    let cancelled = false;
    (async () => {
      try {
        const { library, project } = await refreshLists();
        if (!cancelled) {
          setFiles(library.files);
          setLibraryMeta(library);
          setProjectFiles(project.files);
          setProjectMeta(project);
        }
      } catch (e) {
        if (!cancelled) {
          setFiles([]);
          setProjectFiles([]);
          setLibraryMeta(EMPTY_LIBRARY);
          setProjectMeta(EMPTY_LIBRARY);
          reportError(e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshLists, reportError]);

  // Refetch instantly when the composer finishes uploading attachments to the
  // workspace (fired by useMessageSubmit after a successful upload), so the
  // panel updates without waiting for the 2.5s processing poll below.
  useEffect(() => {
    const handler = () => {
      setPendingIds(readPendingFileSelection());
      refreshLists()
        .then(({ library, project }) => {
          setFiles(library.files);
          setLibraryMeta(library);
          setProjectFiles(project.files);
          setProjectMeta(project);
        })
        .catch((e) => handleAuthError(e)); // stay quiet on transient errors
    };
    window.addEventListener(FILES_CHANGED_EVENT, handler);
    // Also refetch when the window regains focus: rows can change server-side
    // while the tab is idle (background extraction, another tab, ops actions),
    // and a stale "Gagal" row once sent the founder into a dead-end retry.
    window.addEventListener('focus', handler);
    return () => {
      window.removeEventListener(FILES_CHANGED_EVENT, handler);
      window.removeEventListener('focus', handler);
    };
  }, [refreshLists, handleAuthError]);

  // While anything is still "processing", poll every 2.5s. Stops when nothing is
  // processing, on unmount, or when the conversation changes.
  const hasProcessing =
    files.some((f) => f.status === 'processing') ||
    projectFiles.some((f) => f.status === 'processing');
  useEffect(() => {
    if (!hasProcessing) return;
    let cancelled = false;
    const id = window.setInterval(async () => {
      try {
        const { library, project } = await refreshLists();
        if (!cancelled) {
          setFiles(library.files);
          setLibraryMeta(library);
          setProjectFiles(project.files);
          setProjectMeta(project);
        }
      } catch (e) {
        if (!cancelled) handleAuthError(e); // stay quiet on transient poll errors
      }
    }, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [refreshLists, hasProcessing, handleAuthError]);

  // Upload each file sequentially, showing a per-file in-flight row. Allowed
  // with or without a conversation; when one is active the server auto-selects
  // the upload into it, so we just trust the returned `selected` flag.
  const doUpload = useCallback(
    async (fileList: File[], folder?: string | null, forProject = false) => {
      if (fileList.length === 0) return;
      const cid = conversationId;
      setError(null);
      for (const file of fileList) {
        const tempId = `${Date.now()}-${Math.random()}-${file.name}`;
        setUploading((prev) => [...prev, { tempId, filename: file.name, size: file.size }]);
        try {
          const meta = await uploadFile(cid, file, forProject ? projectId : null, folder || null);
          if (forProject) {
            setProjectFiles((prev) => [...prev.filter((f) => f.id !== meta.id), meta]);
          } else {
            setFiles((prev) => [...prev.filter((f) => f.id !== meta.id), meta]);
          }
          if (!forProject && cid == null) {
            // Upload sebelum chat dimulai ikut terpilih untuk chat baru,
            // konsisten dengan auto-select saat upload di percakapan aktif.
            const current = readPendingFileSelection();
            const next = current.includes(meta.id) ? current : [...current, meta.id].slice(0, 20);
            updatePending(next);
          } else {
            notifyFilesChanged();
          }
        } catch (e) {
          reportError(e);
        } finally {
          setUploading((prev) => prev.filter((u) => u.tempId !== tempId));
        }
      }
    },
    [conversationId, projectId, reportError, updatePending]
  );

  // Optimistic delete; restore the row (in created_at order) if it fails.
  const handleDelete = useCallback(
    async (target: FileMeta) => {
      const fromProject = projectFiles.some((f) => f.id === target.id);
      setFiles((prev) => prev.filter((f) => f.id !== target.id));
      setProjectFiles((prev) => prev.filter((f) => f.id !== target.id));
      try {
        await deleteFile(target.id);
      } catch (e) {
        const restore = (prev: FileMeta[]) =>
          prev.some((f) => f.id === target.id)
            ? prev
            : [...prev, target].sort((a, b) => a.created_at.localeCompare(b.created_at));
        if (fromProject) setProjectFiles(restore);
        else setFiles(restore);
        reportError(e);
      }
    },
    [projectFiles, reportError]
  );

  const handleDownload = useCallback(
    async (target: FileMeta) => {
      try {
        const url = await getDownloadUrl(target.id);
        if (url) window.open(url, '_blank', 'noopener');
      } catch (e) {
        reportError(e);
      }
    },
    [reportError]
  );

  // Retry a failed ('error') extraction. Optimistically flip the row to
  // 'processing' (the spinner row) so it feels instant, then let the existing
  // processing poll take over once the server confirms. On failure revert the
  // row to 'error' and surface `detail` inline.
  const handleReprocess = useCallback(
    async (target: FileMeta) => {
      setError(null);
      setFiles((prev) =>
        prev.map((f) => (f.id === target.id ? { ...f, status: 'processing' as const } : f))
      );
      setProjectFiles((prev) =>
        prev.map((f) => (f.id === target.id ? { ...f, status: 'processing' as const } : f))
      );
      try {
        const meta = await reprocessFile(target.id);
        // Trust the server row but keep the local selection flag (the reprocess
        // response is conversation-agnostic and comes back selected=false).
        setFiles((prev) =>
          prev.map((f) => (f.id === target.id ? { ...meta, selected: f.selected } : f))
        );
        setProjectFiles((prev) =>
          prev.map((f) => (f.id === target.id ? { ...meta, selected: f.selected } : f))
        );
      } catch (e) {
        // 409 = the row was not actually in "error" anymore (its state changed
        // server-side while the panel showed a stale row). That is not a user
        // error: silently refetch the truth instead of surfacing dev-speak.
        if (e instanceof FileWorkspaceError && e.status === 409) {
          try {
            const { library, project } = await refreshLists();
            setFiles(library.files);
            setLibraryMeta(library);
            setProjectFiles(project.files);
            setProjectMeta(project);
          } catch {
            /* transient; the poll or next focus refetch will catch up */
          }
          return;
        }
        setFiles((prev) =>
          prev.map((f) => (f.id === target.id ? { ...f, status: 'error' as const } : f))
        );
        setProjectFiles((prev) =>
          prev.map((f) => (f.id === target.id ? { ...f, status: 'error' as const } : f))
        );
        reportError(e);
      }
    },
    [refreshLists, reportError]
  );

  // Optimistic selection toggle. Flip the row immediately, spin its control, and
  // reconcile with the server's confirmed flag. On failure (incl. the 20-file
  // cap 403) revert the row and surface `detail` inline. Chat baru writes the
  // choice locally; the first send applies it to the new conversation.
  const handleToggleSelection = useCallback(
    async (target: FileMeta) => {
      if (conversationId == null) {
        const current = readPendingFileSelection();
        const next = current.includes(target.id)
          ? current.filter((id) => id !== target.id)
          : [...current, target.id].slice(0, 20);
        updatePending(next);
        return;
      }
      const cid = conversationId;
      const next = !target.selected;
      setError(null);
      setFiles((prev) => prev.map((f) => (f.id === target.id ? { ...f, selected: next } : f)));
      setTogglingIds((prev) => [...prev, target.id]);
      try {
        const confirmed = await setFileSelection(target.id, cid, next);
        setFiles((prev) =>
          prev.map((f) => (f.id === target.id ? { ...f, selected: confirmed } : f))
        );
        notifyFilesChanged();
      } catch (e) {
        setFiles((prev) =>
          prev.map((f) => (f.id === target.id ? { ...f, selected: target.selected } : f))
        );
        reportError(e);
      } finally {
        setTogglingIds((prev) => prev.filter((id) => id !== target.id));
      }
    },
    [conversationId, reportError, updatePending]
  );

  const applyRow = (row: FileMeta) => {
    setFiles((prev) => prev.map((f) => (f.id === row.id ? { ...f, ...row, selected: f.selected } : f)));
    setProjectFiles((prev) => prev.map((f) => (f.id === row.id ? { ...f, ...row, selected: f.selected } : f)));
  };

  const handleRename = useCallback(
    async (target: FileMeta, filename: string) => {
      const name = filename.trim();
      if (!name || name === target.filename) return;
      try {
        const row = await patchFile(target.id, { filename: name });
        applyRow(row);
      } catch (e) {
        reportError(e);
      }
    },
    [reportError]
  );

  const handleMove = useCallback(
    async (target: FileMeta, folder: string | null) => {
      try {
        const row = await patchFile(target.id, { folder });
        applyRow(row);
      } catch (e) {
        reportError(e);
      }
    },
    [reportError]
  );

  const handleCreateFolder = useCallback(
    async (path: string, forProject: boolean) => {
      try {
        await createFolder(path, forProject ? projectId : null);
        const { library, project } = await refreshLists();
        setFiles(library.files);
        setLibraryMeta(library);
        setProjectFiles(project.files);
        setProjectMeta(project);
      } catch (e) {
        reportError(e);
      }
    },
    [projectId, refreshLists, reportError]
  );

  const handleDeleteFolder = useCallback(
    async (path: string, forProject: boolean) => {
      try {
        await deleteFolder(path, forProject ? projectId : null);
        const { library, project } = await refreshLists();
        setFiles(library.files);
        setLibraryMeta(library);
        setProjectFiles(project.files);
        setProjectMeta(project);
      } catch (e) {
        reportError(e);
      }
    },
    [projectId, refreshLists, reportError]
  );

  return {
    files,
    projectFiles,
    libraryMeta,
    projectMeta,
    uploading,
    error,
    togglingIds,
    pendingIds,
    doUpload,
    handleDelete,
    handleDownload,
    handleReprocess,
    handleToggleSelection,
    handleRename,
    handleMove,
    handleCreateFolder,
    handleDeleteFolder,
  };
}

// ---------------------------------------------------------------------------
// Hasil (AI-generated artifacts) derived purely client-side from the loaded
// assistant messages. No network. Two kinds:
//   - Presentation decks: markdown links whose href points at a hosted HTML deck
//     (/uploads/presentations/....html).
//   - Generated images: /uploads/auto_generated_....(png|jpg|jpeg|webp) URLs.
// ---------------------------------------------------------------------------
interface DeckItem {
  href: string;
  title: string;
}

// Markdown link: [text](href)
const MD_LINK_RE = /\[([^\]]*)\]\(([^)]+)\)/g;
const DECK_HREF_RE = /\/uploads\/presentations\/.+\.html/i;
// Full generated-image URL (protocol through the file extension).
const GEN_IMG_RE = /https?:\/\/[^\s)<>"']*\/uploads\/auto_generated_[\w.-]+\.(?:png|jpe?g|webp)/gi;

function useHasilArtifacts(messages?: MessageWithCitations[]) {
  return useMemo(() => {
    const decks: DeckItem[] = [];
    const images: string[] = [];
    const seenDeck = new Set<string>();
    const seenImg = new Set<string>();
    for (const m of messages || []) {
      if (m.role !== 'assistant' || !m.content) continue;
      const content = m.content;

      MD_LINK_RE.lastIndex = 0;
      let lm: RegExpExecArray | null;
      while ((lm = MD_LINK_RE.exec(content)) !== null) {
        const href = (lm[2] || '').trim();
        if (!DECK_HREF_RE.test(href) || seenDeck.has(href)) continue;
        seenDeck.add(href);
        let title = (lm[1] || '').trim();
        if (!title) {
          const base = href.split('?')[0].split('#')[0].split('/').pop() || 'Presentasi';
          try { title = decodeURIComponent(base); } catch { title = base; }
        }
        decks.push({ href, title });
      }

      GEN_IMG_RE.lastIndex = 0;
      let im: RegExpExecArray | null;
      while ((im = GEN_IMG_RE.exec(content)) !== null) {
        const url = im[0];
        if (seenImg.has(url)) continue;
        seenImg.add(url);
        images.push(url);
      }
    }
    return { decks, images };
  }, [messages]);
}

const openInNewTab = (url: string) => {
  if (url) window.open(url, '_blank', 'noopener');
};

const KIND_LABEL: Record<string, string> = {
  identity: 'Identitas',
  preference: 'Preferensi',
  project: 'Project',
  fact: 'Fakta',
};

function useActiveMemories(): { content: string; kind: string }[] {
  const [rows, setRows] = useState<{ content: string; kind: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const tokenType = localStorage.getItem('tokenType') || 'Bearer';
        if (!token) return;
        const res = await fetch(`${config.apiUrl}/memories`, {
          headers: { Authorization: `${tokenType} ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data.enabled === false) {
          setRows([]);
          return;
        }
        setRows(Array.isArray(data.memories) ? data.memories : []);
      } catch {
        /* inspector stays useful without the memory list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return rows;
}

function mergeContextSources(
  fromTurn: InspectorSource[],
  files: FileMeta[],
  memories: { content: string; kind: string }[],
): InspectorSource[] {
  const out = [...fromTurn];
  const seenFile = new Set(
    fromTurn.filter((source) => source.kind === 'file').map((source) => source.title.toLowerCase()),
  );
  for (const file of files) {
    const name = (file.filename || '').trim();
    if (!name || seenFile.has(name.toLowerCase())) continue;
    seenFile.add(name.toLowerCase());
    out.push({ kind: 'file', title: name, meta: 'file' });
  }
  const seenMem = new Set(
    fromTurn.filter((source) => source.kind === 'memory').map((source) => source.title),
  );
  for (const memory of memories) {
    const text = (memory.content || '').trim();
    if (!text || seenMem.has(text)) continue;
    seenMem.add(text);
    out.push({
      kind: 'memory',
      title: text,
      meta: KIND_LABEL[memory.kind] || 'memori',
    });
  }
  return out;
}

const Chevron: React.FC<{ open: boolean; color: string }> = ({ open, color }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: open ? 'rotate(90deg)' : 'none',
      transition: 'transform 0.15s ease',
      flexShrink: 0,
    }}
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const GlobeGlyph: React.FC<{ color: string }> = ({ color }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </svg>
);

const SourcesGlyph: React.FC<{ color: string }> = ({ color }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 6h13M8 12h13M8 18h13" />
    <circle cx="4" cy="6" r="1.2" fill={color} stroke="none" />
    <circle cx="4" cy="12" r="1.2" fill={color} stroke="none" />
    <circle cx="4" cy="18" r="1.2" fill={color} stroke="none" />
  </svg>
);

const FileGlyph: React.FC<{ color: string }> = ({ color }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6" />
  </svg>
);

const MemoryGlyph: React.FC<{ color: string }> = ({ color }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 19.5c.8-3.2 3.4-5 7-5s6.2 1.8 7 5" />
    <circle cx="12" cy="8" r="1.2" />
  </svg>
);

const BoxGlyph: React.FC<{ color: string }> = ({ color }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 8 12 3 3 8l9 5 9-5Z" />
    <path d="M3 8v8l9 5 9-5V8" />
    <path d="M12 13v8" />
  </svg>
);

const FaviconStack: React.FC<{ hosts: string[]; border: string }> = ({ hosts, border }) => {
  const shown = Array.from(new Set(hosts.filter(Boolean))).slice(0, 3);
  if (shown.length === 0) return null;
  return (
    <span style={{ display: 'flex', flexDirection: 'row-reverse', marginRight: '2px' }}>
      {shown.slice().reverse().map((host, i) => (
        <img
          key={`${host}-${i}`}
          src={faviconUrl(host)}
          alt=""
          width={14}
          height={14}
          style={{
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            marginLeft: i === 0 ? 0 : '-5px',
            border: `1.5px solid ${border}`,
            background: border,
            flexShrink: 0,
          }}
        />
      ))}
    </span>
  );
};

const AccordionRow: React.FC<{
  c: any;
  icon: React.ReactNode;
  label: string;
  count?: number | string;
  open: boolean;
  onToggle: () => void;
  nested?: boolean;
  trailing?: React.ReactNode;
}> = ({ c, icon, label, count, open, onToggle, nested, trailing }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      width: '100%',
      height: '34px',
      padding: nested ? '0 4px 0 22px' : '0 4px',
      background: 'none',
      border: 0,
      color: c.ink,
      fontFamily: 'inherit',
      fontSize: '13px',
      fontWeight: 500,
      cursor: 'pointer',
      textAlign: 'left',
      borderRadius: '8px',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.panel2; }}
    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
  >
    <span style={{ display: 'flex', flexShrink: 0, color: c.ink2 }}>{icon}</span>
    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </span>
    {trailing}
    {count != null && (
      <span style={{ fontFamily: c.mono, fontSize: '11.5px', color: c.ink3, flexShrink: 0 }}>
        {count}
      </span>
    )}
    <Chevron open={open} color={c.ink3} />
  </button>
);

const InspectorProgress: React.FC<{
  c: any;
  steps: { step_id: number; label: string; detail?: string; status: 'active' | 'done' }[];
  streaming: boolean;
  durationMs: number | null;
}> = ({ c, steps, streaming, durationMs }) => {
  const [open, setOpen] = useState(false);
  const expanded = streaming || open;
  const doneSecs = durationMs != null ? durationMs / 1000 : null;
  if (steps.length === 0) return null;

  return (
    <div>
      <SectionLabel color={c.ink3}>Progress</SectionLabel>
      {expanded ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {steps.map((step) => {
            const done = step.status === 'done';
            return (
              <div
                key={step.step_id}
                className="pa-inspector-step"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  padding: '4px 0',
                  fontSize: '12.5px',
                  color: done ? c.ink3 : c.ink,
                }}
              >
                {done ? (
                  <span style={{
                    width: '14px', height: '14px', borderRadius: '50%',
                    background: c.jadeDim, color: c.jade, display: 'grid',
                    placeItems: 'center', fontSize: '9px', flexShrink: 0, marginTop: '1px',
                  }}>✓</span>
                ) : (
                  <span className="pa-inspector-ping" style={{
                    width: '14px', height: '14px', borderRadius: '50%',
                    border: `2px solid ${c.jade}`, flexShrink: 0, marginTop: '1px',
                  }} />
                )}
                <span style={{
                  textDecoration: done ? 'line-through' : 'none',
                  textDecorationColor: 'rgba(121,127,136,0.7)',
                  minWidth: 0,
                }}>
                  {localizeStepLabel(step.label)}
                  {step.detail && (
                    <span style={{
                      display: 'block',
                      fontFamily: c.mono,
                      fontSize: '11px',
                      color: c.ink3,
                      fontWeight: 400,
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {step.detail}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            background: 'none',
            border: 0,
            padding: '4px 0',
            fontSize: '12.5px',
            color: c.ink2,
            fontFamily: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span style={{
            width: '14px', height: '14px', borderRadius: '50%',
            background: c.jadeDim, color: c.jade, display: 'grid',
            placeItems: 'center', fontSize: '9px', flexShrink: 0,
          }}>✓</span>
          {steps.length} langkah selesai
          {doneSecs != null && (
            <span style={{ marginLeft: 'auto', fontFamily: c.mono, fontSize: '10.5px', color: c.ink3 }}>
              {doneSecs.toFixed(1)}s
            </span>
          )}
        </button>
      )}
    </div>
  );
};

const SourceList: React.FC<{
  c: any;
  sources: InspectorSource[];
  onFileClick?: (name: string) => void;
}> = ({ c, sources, onFileClick }) => (
  <div
    className="custom-scrollbar"
    style={{
      maxHeight: '220px',
      overflowY: 'auto',
      padding: '2px 0 6px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: '1px',
    }}
  >
    {sources.map((source, i) => {
      const inner = (
        <>
          {source.kind === 'web' && source.href && (
            <img
              src={faviconUrl(source.title)}
              alt=""
              width={14}
              height={14}
              style={{
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                flexShrink: 0,
                background: c.panel2,
              }}
            />
          )}
          <span style={{
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
          }}>
            <span style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: c.ink,
              fontSize: '12.5px',
            }}>
              {source.title}
            </span>
            {source.meta && source.meta !== 'web' && source.meta !== 'file' && source.meta !== 'memori' && (
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: c.ink3,
                fontSize: '11px',
                fontFamily: source.kind === 'web' ? c.mono : 'inherit',
              }}>
                {source.meta}
              </span>
            )}
          </span>
        </>
      );
      const rowStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 8px',
        borderRadius: '8px',
        textDecoration: 'none',
        minWidth: 0,
      };
      if (source.href) {
        return (
          <a
            key={`${source.kind}-${source.href}-${i}`}
            href={source.href}
            target="_blank"
            rel="noopener noreferrer"
            style={rowStyle}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.panel2; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {inner}
          </a>
        );
      }
      if (source.kind === 'file' && onFileClick) {
        return (
          <button
            key={`${source.kind}-${source.title}-${i}`}
            type="button"
            onClick={() => onFileClick(source.title)}
            style={{ ...rowStyle, width: '100%', background: 'none', border: 0, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.panel2; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {inner}
          </button>
        );
      }
      return (
        <div key={`${source.kind}-${source.title}-${i}`} style={rowStyle}>
          {inner}
        </div>
      );
    })}
  </div>
);

const InspectorSources: React.FC<{
  c: any;
  sources: InspectorSource[];
  onFileClick?: (name: string) => void;
}> = ({ c, sources, onFileClick }) => {
  const grouped = groupInspectorSources(sources);
  const [open, setOpen] = useState(true);
  const [openKind, setOpenKind] = useState<'web' | 'file' | 'memory' | null>(null);
  if (sources.length === 0) return null;

  const toggleKind = (kind: 'web' | 'file' | 'memory') => {
    setOpenKind((current) => (current === kind ? null : kind));
  };

  const rows = (
    [
      { kind: 'web' as const, label: 'Web', icon: <GlobeGlyph color="currentColor" />, items: grouped.web },
      { kind: 'file' as const, label: 'File', icon: <FileGlyph color="currentColor" />, items: grouped.file },
      { kind: 'memory' as const, label: 'Memori', icon: <MemoryGlyph color="currentColor" />, items: grouped.memory },
    ]
  ).filter((row) => row.items.length > 0);

  return (
    <div>
      <AccordionRow
        c={c}
        icon={<SourcesGlyph color="currentColor" />}
        label="Sumber"
        count={sources.length}
        open={open}
        onToggle={() => setOpen((value) => !value)}
      />
      {open && (
        <div>
          {rows.map((row) => (
            <div key={row.kind}>
              <AccordionRow
                c={c}
                icon={row.icon}
                label={row.label}
                count={row.items.length}
                open={openKind === row.kind}
                onToggle={() => toggleKind(row.kind)}
                nested
                trailing={row.kind === 'web' ? (
                  <FaviconStack
                    hosts={row.items.map((item) => item.title)}
                    border={c.panel}
                  />
                ) : undefined}
              />
              {openKind === row.kind && (
                <SourceList c={c} sources={row.items} onFileClick={onFileClick} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InspectorHasil: React.FC<{
  c: any;
  decks: DeckItem[];
  images: string[];
}> = ({ c, decks, images }) => {
  const count = decks.length + images.length;
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <div>
      <AccordionRow
        c={c}
        icon={<BoxGlyph color="currentColor" />}
        label="Hasil"
        count={count}
        open={open}
        onToggle={() => setOpen((value) => !value)}
      />
      {open && (
        <div style={{ padding: '4px 0 8px 4px' }}>
          {decks.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {decks.map((d) => (
                <div
                  key={`deck-${d.href}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openInNewTab(d.href)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openInNewTab(d.href);
                    }
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.panel2; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '9px',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ flexShrink: 0, display: 'flex' }}>
                    <SlidesIcon color={c.ink3} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
                    <span
                      title={d.title}
                      style={{
                        fontSize: '13px',
                        color: c.ink,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {d.title}
                    </span>
                    <span style={{ fontSize: '11px', color: c.ink3 }}>Presentasi</span>
                  </div>
                  <span style={{ flexShrink: 0, display: 'flex', color: c.ink3 }}>
                    <ArrowUpRight color="currentColor" />
                  </span>
                </div>
              ))}
            </div>
          )}
          {images.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '6px',
                marginTop: decks.length > 0 ? '8px' : 0,
                padding: '0 2px',
              }}
            >
              {images.map((url) => (
                <button
                  key={url}
                  type="button"
                  aria-label="Buka gambar"
                  title="Buka gambar"
                  onClick={() => openInNewTab(url)}
                  style={{
                    aspectRatio: '1 / 1',
                    width: '100%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: `1px solid ${c.line}`,
                    background: c.panel,
                    padding: 0,
                    cursor: 'pointer',
                    display: 'block',
                  }}
                >
                  <img
                    src={url}
                    alt=""
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const RightSidebar: React.FC<RightSidebarProps> = ({
  theme,
  themeColors,
  currentConversationId,
  projectId = null,
  messages,
  overlay = false,
}) => {
  const c = themeColors[theme];
  const {
    files,
    projectFiles,
    libraryMeta,
    projectMeta,
    uploading,
    error,
    togglingIds,
    pendingIds,
    doUpload,
    handleDelete,
    handleDownload,
    handleReprocess,
    handleToggleSelection,
    handleRename,
    handleMove,
    handleCreateFolder,
    handleDeleteFolder,
  } = useFileWorkspace(currentConversationId, projectId);
  const { decks, images } = useHasilArtifacts(messages);
  const inspect = useMemo(() => inspectFromMessages(messages), [messages]);
  const memories = useActiveMemories();
  const wasStreaming = useRef(false);

  useEffect(() => {
    const open = () => setOverlayCollapsed(false);
    window.addEventListener(OPEN_INSPECTOR_EVENT, open);
    return () => window.removeEventListener(OPEN_INSPECTOR_EVENT, open);
  }, []);

  useEffect(() => {
    if (inspect.streaming && !wasStreaming.current && overlay) {
      setOverlayCollapsed(false);
    }
    wasStreaming.current = inspect.streaming;
  }, [inspect.streaming, overlay]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const pickScope = useRef<'library' | 'project'>('library');
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  // Overlay mode (narrow windows): the panel starts COLLAPSED into a slim
  // edge tab so it never covers the thread uninvited; the tab expands it,
  // the edge chevron collapses it again. Docked mode is always expanded.
  const [overlayCollapsed, setOverlayCollapsed] = useState(true);
  useEffect(() => {
    if (!overlay) setOverlayCollapsed(false);
    else setOverlayCollapsed(true);
  }, [overlay]);
  const [pickHover, setPickHover] = useState(false);
  const [query, setQuery] = useState('');
  const [libraryFolder, setLibraryFolder] = useState<string | null>(null);
  const [projectFolder, setProjectFolder] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<FileMeta | null>(null);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [newFolderFor, setNewFolderFor] = useState<'library' | 'project' | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [deskOpen, setDeskOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<
    { kind: 'file'; id: number } | { kind: 'folder'; path: string; project: boolean } | null
  >(null);
  const [movingId, setMovingId] = useState<number | null>(null);
  const [dropFolder, setDropFolder] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const movingDrag = useRef<{ id: number; scope: 'library' | 'project' } | null>(null);
  const [pendingMoveId, setPendingMoveId] = useState<number | null>(null);
  const [filePeekOpen, setFilePeekOpen] = useState(false);
  useEffect(() => {
    setFilePeekOpen(false);
  }, [currentConversationId]);

  const openDesk = useCallback((file?: FileMeta) => {
    setConfirmDelete(null);
    if (file) setPreviewFile(file);
    setDeskOpen(true);
  }, []);

  const closeDesk = useCallback(() => {
    setDeskOpen(false);
    setPreviewFile(null);
    setConfirmDelete(null);
  }, []);

  useEffect(() => {
    if (!confirmDelete && movingId == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setConfirmDelete(null);
      setMovingId(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [confirmDelete, movingId]);

  // A conversation is what a file can be selected "into". Upload works either
  // way, but the per-conversation selection controls only appear with one.
  const hasConversation = currentConversationId != null;

  // Split the library into the two rendered groups. `selected` always comes back
  // false without a conversation, so everything lands in LIBRARY there.
  const isChosen = (f: FileMeta) =>
    hasConversation ? f.selected : pendingIds.includes(f.id);
  const selectedFiles = files.filter(isChosen);
  const libraryFiles = files.filter((f) => !isChosen(f));
  const selectedCount = selectedFiles.length;
  const needle = query.trim().toLowerCase();
  const matchName = (f: FileMeta) =>
    !needle || f.filename.toLowerCase().includes(needle);
  const visibleSelected = selectedFiles.filter(matchName);
  const visibleLibrary = libraryFiles.filter(matchName);
  const visibleProject = projectFiles.filter(matchName);
  const activeCount = selectedCount + projectFiles.length;

  const hasilCount = decks.length + images.length;
  const contextSources = useMemo(
    () => mergeContextSources(inspect.sources, [...selectedFiles, ...projectFiles], memories),
    [inspect.sources, selectedFiles, projectFiles, memories],
  );
  const hasContext = contextSources.length > 0;
  const hasRun = inspect.steps.length > 0 || inspect.sources.length > 0 || inspect.streaming;
  const hasItems =
    files.length > 0 ||
    projectFiles.length > 0 ||
    libraryMeta.folders.length > 0 ||
    projectMeta.folders.length > 0 ||
    uploading.length > 0 ||
    hasilCount > 0 ||
    hasRun ||
    hasContext ||
    newFolderFor != null;
  const showSearch = files.length + projectFiles.length + libraryMeta.folders.length > 0;

  const openPicker = (scope: 'library' | 'project') => {
    pickScope.current = scope;
    inputRef.current?.click();
  };

  const handlePick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = e.target.files ? Array.from(e.target.files) : [];
      e.target.value = '';
      const forProject = pickScope.current === 'project';
      void doUpload(list, forProject ? projectFolder : libraryFolder, forProject);
    },
    [doUpload, libraryFolder, projectFolder]
  );

  // Whole-panel drop target. A depth counter keeps dragOver stable while the
  // pointer moves across the panel's nested children (naive dragleave flickers).
  // Upload is allowed with or without an active conversation (library upload).
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (movingDrag.current) return;
    dragDepth.current += 1;
    setDragOver(true);
  }, []);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (movingDrag.current) return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragOver(false);
    }
  }, []);
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      dragDepth.current = 0;
      setDragOver(false);
      setDropFolder(null);
      if (movingDrag.current) return;
      const list = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
      void doUpload(list, libraryFolder);
    },
    [doUpload, libraryFolder]
  );

  const moveDestinations = (scope: 'library' | 'project', current: string | null) => {
    const all = scope === 'project' ? projectMeta.folders : libraryMeta.folders;
    const dests: { path: string | null; label: string }[] = [];
    if (current) dests.push({ path: null, label: 'Folder utama' });
    [...all].sort((a, b) => a.localeCompare(b, 'id')).forEach((path) => {
      if (path !== current) dests.push({ path, label: path });
    });
    return dests;
  };

  const acceptFileDrop = (
    e: React.DragEvent,
    dest: string | null,
    scope: 'library' | 'project',
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const drag = movingDrag.current;
    setDropFolder(null);
    if (!drag || drag.scope !== scope) return;
    const list = scope === 'project' ? projectFiles : files;
    const target = list.find((row) => row.id === drag.id);
    if (!target || (target.folder || null) === dest) return;
    void handleMove(target, dest);
  };

  const rowBase: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '9px 10px',
    borderRadius: '8px',
  };
  const nameStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    fontSize: '13px',
    color: c.ink,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  // A single file row. `muted` drops the filename a notch (ink3) for LIBRARY
  // rows so the AKTIF group reads as the primary, in-use set. The 16px selection
  // control only appears when a conversation is active.
  const renderFileRow = (f: FileMeta, muted: boolean, locked = false) => {
    const hovered = hoveredId === f.id;
    const confirming = confirmDelete?.kind === 'file' && confirmDelete.id === f.id;
    const moving = movingId === f.id;
    const scope: 'library' | 'project' = locked ? 'project' : 'library';
    const isError = f.status === 'error';
    // Failed rows explain themselves on hover: the recorded reason, else a
    // generic fallback.
    const errorReason = (f.summary && f.summary.trim()) || 'Ekstraksi gagal';
    const dests = moving ? moveDestinations(scope, f.folder || null) : [];
    const browse = scope === 'project' ? projectFolder : libraryFolder;
    const canCreateHere = folderDepth(browse) < 3;
    return (
      <div
        key={f.id}
        draggable={!renamingId && !confirming}
        onDragStart={(e) => {
          if ((e.target as HTMLElement).closest('[data-no-drag]')) {
            e.preventDefault();
            return;
          }
          movingDrag.current = { id: f.id, scope };
          e.dataTransfer.effectAllowed = 'move';
          setDraggingId(f.id);
          setMovingId(null);
        }}
        onDragEnd={() => {
          movingDrag.current = null;
          setDraggingId(null);
          setDropFolder(null);
        }}
        onMouseEnter={() => setHoveredId(f.id)}
        onMouseLeave={() => setHoveredId((cur) => (cur === f.id ? null : cur))}
        title={isError ? errorReason : undefined}
        style={{
          ...rowBase,
          position: 'relative',
          opacity: draggingId === f.id ? 0.45 : 1,
          backgroundColor:
            previewFile?.id === f.id
              ? c.jadeDim
              : hovered || moving
                ? c.panel2
                : 'transparent',
          transition: 'background-color 0.12s ease',
        }}
      >
        {locked ? (
          <span
            title="Selalu dibaca di chat project ini"
            style={{
              width: '16px',
              height: '16px',
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              background: c.jadeDim,
              color: c.jade,
            }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        ) : (
          <SelectionControl
            selected={hasConversation ? f.selected : pendingIds.includes(f.id)}
            busy={togglingIds.includes(f.id)}
            c={c}
            onToggle={() => handleToggleSelection(f)}
          />
        )}
        <span style={{ flexShrink: 0, display: 'flex' }}>
          <FileTypeIcon family={fileFamily(f.mime, f.filename)} color={c.ink3} />
        </span>
        {renamingId === f.id ? (
          <input
            autoFocus
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onBlur={() => {
              void handleRename(f, renameDraft);
              setRenamingId(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleRename(f, renameDraft);
                setRenamingId(null);
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setRenamingId(null);
              }
            }}
            style={{
              ...nameStyle,
              background: c.panel,
              border: `1px solid ${c.line2}`,
              borderRadius: '6px',
              padding: '2px 6px',
              color: c.ink,
              fontFamily: 'inherit',
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => openDesk(f)}
            onDoubleClick={() => {
              setRenamingId(f.id);
              setRenameDraft(f.filename);
            }}
            style={{
              ...nameStyle,
              color: muted ? c.ink3 : c.ink,
              background: 'none',
              border: 'none',
              padding: 0,
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
            title={isError ? errorReason : `Lihat ${f.filename}`}
          >
            {f.filename}
          </button>
        )}

        {confirming ? (
          <div data-no-drag="1">
          <ConfirmDeleteActions
            danger={ERROR_RED}
            muted={c.ink3}
            onConfirm={() => {
              setConfirmDelete(null);
              if (previewFile?.id === f.id) setPreviewFile(null);
              void handleDelete(f);
            }}
            onCancel={() => setConfirmDelete(null)}
          />
          </div>
        ) : isError ? (
          // Failed rows: actions ALWAYS visible (not hover-gated) so the dead-end
          // is escapable. "Coba lagi" retries extraction; the trash asks first.
          // Size is dropped here to make room.
          <div data-no-drag="1" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <span style={{ fontSize: '11px', color: ERROR_RED }}>Gagal</span>
            <button
              type="button"
              onClick={() => handleReprocess(f)}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: '12px',
                color: c.jade,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              Coba lagi
            </button>
            <IconButton
              label="Hapus"
              color={c.ink3}
              hoverColor={ERROR_RED}
              onClick={() => setConfirmDelete({ kind: 'file', id: f.id })}
            >
              <TrashIcon color="currentColor" />
            </IconButton>
          </div>
        ) : hovered || moving ? (
          <div data-no-drag="1" style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
            <IconButton
              label="Pindah ke folder"
              color={c.ink3}
              hoverColor={c.ink}
              onClick={() => {
                setConfirmDelete(null);
                setMovingId(moving ? null : f.id);
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
                <path d="M12 11v6" />
                <path d="m9 14 3 3 3-3" />
              </svg>
            </IconButton>
            <IconButton
              label="Ganti nama"
              color={c.ink3}
              hoverColor={c.ink}
              onClick={() => {
                setConfirmDelete(null);
                setMovingId(null);
                setRenamingId(f.id);
                setRenameDraft(f.filename);
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
            </IconButton>
            <IconButton label="Unduh" color={c.ink3} hoverColor={c.ink} onClick={() => handleDownload(f)}>
              <DownloadIcon color="currentColor" />
            </IconButton>
            <IconButton
              label="Hapus"
              color={c.ink3}
              hoverColor={ERROR_RED}
              onClick={() => setConfirmDelete({ kind: 'file', id: f.id })}
            >
              <TrashIcon color="currentColor" />
            </IconButton>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {f.status === 'processing' && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: c.ink3 }}>
                <Spinner color={c.ink3} /> Diproses…
              </span>
            )}
            <span style={{ fontSize: '12px', color: c.ink3, whiteSpace: 'nowrap' }}>
              {prettySize(f.size)}
            </span>
          </div>
        )}
        {moving && (
          <div
            data-no-drag="1"
            style={{
              position: 'absolute',
              right: '8px',
              top: '100%',
              zIndex: 8,
              marginTop: '4px',
              minWidth: '180px',
              maxWidth: '240px',
              background: theme === 'dark' ? '#141619' : '#FFFFFF',
              border: `1px solid ${c.line2}`,
              borderRadius: '10px',
              boxShadow: c.shadowSoft,
              padding: '6px',
            }}
          >
            <div style={{ fontSize: '11px', color: c.ink3, padding: '4px 8px 6px' }}>
              Pindah ke
            </div>
            {dests.map((dest) => (
              <button
                key={dest.path || 'root'}
                type="button"
                onClick={() => {
                  setMovingId(null);
                  void handleMove(f, dest.path);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  borderRadius: '7px',
                  padding: '6px 8px',
                  color: c.ink,
                  fontSize: '12.5px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.panel2; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {dest.label}
              </button>
            ))}
            {canCreateHere && (
              <button
                type="button"
                onClick={() => {
                  setMovingId(null);
                  setPendingMoveId(f.id);
                  setNewFolderFor(scope);
                  setNewFolderName('');
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  borderRadius: '7px',
                  padding: '6px 8px',
                  color: c.ink2,
                  fontSize: '12.5px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Folder baru…
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const submitNewFolder = (scope: 'library' | 'project') => {
    const name = newFolderName.trim();
    const moveId = pendingMoveId;
    setPendingMoveId(null);
    if (!name) {
      setNewFolderFor(null);
      return;
    }
    const current = scope === 'library' ? libraryFolder : projectFolder;
    const path = joinFolder(current, name);
    void handleCreateFolder(path, scope === 'project').then(() => {
      if (moveId == null) return;
      const list = scope === 'project' ? projectFiles : files;
      const target = list.find((row) => row.id === moveId);
      if (target) void handleMove(target, path);
    });
    setNewFolderFor(null);
    setNewFolderName('');
  };

  const renderBrowse = (opts: {
    files: FileMeta[];
    folders: string[];
    current: string | null;
    onOpenFolder: (name: string) => void;
    onBack: () => void;
    onNewFolder: () => void;
    newFolder: boolean;
    locked: boolean;
    muted: boolean;
    scope: 'library' | 'project';
  }) => (
    <div>
      {opts.current && (
        <button
          type="button"
          onClick={opts.onBack}
          onDragOver={(e) => {
            if (!movingDrag.current || movingDrag.current.scope !== opts.scope) return;
            e.preventDefault();
            e.stopPropagation();
            setDropFolder('__parent__');
          }}
          onDragLeave={() => {
            setDropFolder((cur) => (cur === '__parent__' ? null : cur));
          }}
          onDrop={(e) => acceptFileDrop(e, parentFolder(opts.current), opts.scope)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: dropFolder === '__parent__' ? c.jadeDim : 'none',
            border: 'none',
            borderRadius: '8px',
            padding: '6px 8px 8px',
            color: c.ink2,
            fontSize: '12px',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          ← {opts.current}
        </button>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {opts.folders.map((name) => {
          const full = joinFolder(opts.current, name);
          const confirmingFolder =
            confirmDelete?.kind === 'folder' && confirmDelete.path === full;
          return (
            <div
              key={`folder-${full}`}
              style={{
                ...rowBase,
                cursor: 'pointer',
                backgroundColor:
                  dropFolder === full || confirmingFolder ? c.jadeDim : 'transparent',
                boxShadow: dropFolder === full ? `inset 0 0 0 1px ${c.jade}` : 'none',
              }}
              onDragOver={(e) => {
                if (!movingDrag.current || movingDrag.current.scope !== opts.scope) return;
                e.preventDefault();
                e.stopPropagation();
                setDropFolder(full);
              }}
              onDragLeave={() => {
                setDropFolder((cur) => (cur === full ? null : cur));
              }}
              onDrop={(e) => acceptFileDrop(e, full, opts.scope)}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.panel2; }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  dropFolder === full || confirmingFolder ? c.jadeDim : 'transparent';
              }}
            >
              <button
                type="button"
                onClick={() => opts.onOpenFolder(name)}
                style={{
                  ...nameStyle,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: c.ink,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
                </svg>
                {name}
              </button>
              {confirmingFolder ? (
                <ConfirmDeleteActions
                  danger={ERROR_RED}
                  muted={c.ink3}
                  onConfirm={() => {
                    setConfirmDelete(null);
                    void handleDeleteFolder(full, opts.scope === 'project');
                  }}
                  onCancel={() => setConfirmDelete(null)}
                />
              ) : (
                <IconButton
                  label="Hapus folder"
                  color={c.ink3}
                  hoverColor={ERROR_RED}
                  onClick={() => setConfirmDelete({
                    kind: 'folder',
                    path: full,
                    project: opts.scope === 'project',
                  })}
                >
                  <TrashIcon color="currentColor" />
                </IconButton>
              )}
            </div>
          );
        })}
        {opts.files.map((f) => renderFileRow(f, opts.muted, opts.locked))}
      </div>
      {opts.newFolder ? (
        <input
          autoFocus
          value={newFolderName}
          placeholder="Nama folder"
          onChange={(e) => setNewFolderName(e.target.value)}
          onBlur={() => submitNewFolder(opts.scope)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitNewFolder(opts.scope);
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setNewFolderFor(null);
            }
          }}
          style={{
            width: '100%',
            height: '30px',
            marginTop: '8px',
            padding: '0 8px',
            borderRadius: '7px',
            border: `1px solid ${c.line2}`,
            background: c.panel,
            color: c.ink,
            fontSize: '12.5px',
            fontFamily: 'inherit',
          }}
        />
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
          {opts.scope === 'project' && (
            <ListAction
              label="Unggah"
              color={c.ink2}
              line={c.line2}
              onClick={() => openPicker('project')}
            >
              <UploadGlyph />
            </ListAction>
          )}
          {folderDepth(opts.current) < 3 && (
            <ListAction
              label="Folder baru"
              color={c.ink2}
              line={c.line2}
              onClick={opts.onNewFolder}
            >
              <FolderPlusGlyph />
            </ListAction>
          )}
        </div>
      )}
    </div>
  );

  // ---- File section: two groups (AKTIF / LIBRARY) + in-flight rows ------
  // A group header only renders when its group is non-empty; with no
  // conversation everything is unselected and only LIBRARY shows.
  const fileSection = (files.length > 0 || projectFiles.length > 0 || uploading.length > 0
    || libraryMeta.folders.length > 0 || projectMeta.folders.length > 0
    || newFolderFor != null) && (
    <div>
      {showSearch && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cari file"
          aria-label="Cari file"
          style={{
            width: '100%',
            height: '32px',
            marginBottom: '14px',
            padding: '0 10px',
            borderRadius: '8px',
            border: `1px solid ${c.line}`,
            background: c.panel,
            color: c.ink,
            fontSize: '12.5px',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
        <ListAction
          label="Unggah"
          color={c.ink2}
          line={c.line2}
          onClick={() => openPicker('library')}
        >
          <UploadGlyph />
        </ListAction>
        {folderDepth(libraryFolder) < 3 && (
          <ListAction
            label="Folder baru"
            color={c.ink2}
            line={c.line2}
            onClick={() => {
              setNewFolderFor('library');
              setNewFolderName('');
            }}
          >
            <FolderPlusGlyph />
          </ListAction>
        )}
      </div>

      {visibleSelected.length > 0 && (
        <div>
          <SectionLabel
            color={c.ink3}
            hint={hasConversation ? 'Power AI baca file ini di chat ini' : 'Ikut ke chat barumu'}
          >
            {hasConversation ? 'Aktif di percakapan ini' : 'Dipakai untuk chat baru'}
          </SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {visibleSelected.map((f) => renderFileRow(f, false))}
          </div>
        </div>
      )}

      {(projectFiles.length > 0 || projectMeta.folders.length > 0 || newFolderFor === 'project') && (
        <div style={{ marginTop: visibleSelected.length > 0 ? '20px' : 0 }}>
          <SectionLabel color={c.ink3} hint="Selalu dibaca di chat dalam project ini">
            File project
          </SectionLabel>
          {renderBrowse({
            files: needle ? visibleProject : filesInFolder(projectFiles, projectFolder),
            folders: needle ? [] : childFolders(projectMeta.folders, projectFolder),
            current: projectFolder,
            onOpenFolder: (name) => setProjectFolder(joinFolder(projectFolder, name)),
            onBack: () => setProjectFolder(parentFolder(projectFolder)),
            onNewFolder: () => {
              setNewFolderFor('project');
              setNewFolderName('');
            },
            newFolder: newFolderFor === 'project',
            locked: true,
            muted: false,
            scope: 'project',
          })}
        </div>
      )}

      {(libraryFiles.length > 0 || libraryMeta.folders.length > 0 || newFolderFor === 'library') && (
        <div style={{ marginTop: visibleSelected.length > 0 || projectFiles.length > 0 || projectMeta.folders.length > 0 ? '20px' : 0 }}>
          <SectionLabel color={c.ink3} hint="Centang supaya dipakai di chat ini">
            Library
          </SectionLabel>
          {renderBrowse({
            files: needle ? visibleLibrary : filesInFolder(libraryFiles, libraryFolder),
            folders: needle ? [] : childFolders(libraryMeta.folders, libraryFolder),
            current: libraryFolder,
            onOpenFolder: (name) => setLibraryFolder(joinFolder(libraryFolder, name)),
            onBack: () => setLibraryFolder(parentFolder(libraryFolder)),
            onNewFolder: () => {
              setNewFolderFor('library');
              setNewFolderName('');
            },
            newFolder: newFolderFor === 'library',
            locked: false,
            muted: true,
            scope: 'library',
          })}
        </div>
      )}

      {needle &&
        visibleSelected.length === 0 &&
        visibleLibrary.length === 0 &&
        visibleProject.length === 0 && (
          <div style={{ fontSize: '12.5px', color: c.ink3, padding: '8px 2px 4px' }}>
            Tidak ada file yang cocok
          </div>
        )}

      {uploading.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            marginTop: files.length > 0 ? '8px' : 0,
          }}
        >
          {uploading.map((u) => (
            <div key={u.tempId} style={{ ...rowBase, opacity: 0.8 }}>
              {hasConversation && <span style={{ width: '16px', flexShrink: 0 }} />}
              <span style={{ flexShrink: 0, display: 'flex' }}>
                <FileTypeIcon family={fileFamily('', u.filename)} color={c.ink3} />
              </span>
              <span style={nameStyle} title={u.filename}>
                {u.filename}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: c.ink3, flexShrink: 0 }}>
                <Spinner color={c.ink3} /> Mengunggah…
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ---- Hasil section (AI-generated decks + images) ---------------------
  const hasilSection = hasilCount > 0 && (
    <div>
      <SectionLabel color={c.ink3}>Hasil</SectionLabel>

      {decks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {decks.map((d) => (
              <div
                key={`deck-${d.href}`}
                role="button"
                tabIndex={0}
                onClick={() => openInNewTab(d.href)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openInNewTab(d.href); } }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.panel2; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                style={{
                  ...rowBase,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.12s ease',
                }}
              >
                <span style={{ flexShrink: 0, display: 'flex' }}>
                  <SlidesIcon color={c.ink3} />
                </span>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  <span
                    title={d.title}
                    style={{
                      fontSize: '13px',
                      color: c.ink,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {d.title}
                  </span>
                  <span style={{ fontSize: '11px', color: c.ink3 }}>Presentasi</span>
                </div>
                <span style={{ flexShrink: 0, display: 'flex', color: c.ink3 }}>
                  <ArrowUpRight color="currentColor" />
                </span>
              </div>
          ))}
        </div>
      )}

      {images.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '6px',
            marginTop: decks.length > 0 ? '10px' : '0',
            padding: '0 2px',
          }}
        >
          {images.map((url) => (
            <button
              key={url}
              type="button"
              aria-label="Buka gambar"
              title="Buka gambar"
              onClick={() => openInNewTab(url)}
              style={{
                aspectRatio: '1 / 1',
                width: '100%',
                borderRadius: '8px',
                overflow: 'hidden',
                border: `1px solid ${c.line}`,
                background: c.panel,
                padding: 0,
                cursor: 'pointer',
                display: 'block',
              }}
            >
              <img
                src={url}
                alt=""
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ---- Empty states ----------------------------------------------------
  const emptyState = (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '24px 24px 40px',
      }}
    >
      <WorkspaceIllustration c={c} active={dragOver} />
      <div style={{ fontSize: '14px', fontWeight: 600, color: c.ink, marginTop: '18px' }}>
        File
      </div>

      {!hasConversation ? (
        <>
          <div
            style={{
              fontSize: '12.5px',
              color: c.ink3,
              lineHeight: 1.55,
              maxWidth: '230px',
              marginTop: '8px',
            }}
          >
            Upload PDF, Word, Excel, atau gambar. Centang file supaya Power AI membacanya di chat barumu.
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '6px',
              marginTop: '18px',
            }}
          >
            <TypePill c={c} label="PDF" />
            <TypePill c={c} label="Word" />
            <TypePill c={c} label="Excel" />
            <TypePill c={c} label="Gambar" />
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onMouseEnter={() => setPickHover(true)}
            onMouseLeave={() => setPickHover(false)}
            style={{
              marginTop: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              height: '34px',
              padding: '0 14px',
              borderRadius: '9px',
              background: c.panel,
              border: `1px solid ${pickHover ? c.line2 : c.line}`,
              color: c.ink,
              fontSize: '12.5px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'border-color 0.15s ease',
            }}
          >
            Pilih file
          </button>
          <button
            type="button"
            onClick={() => {
              setNewFolderFor('library');
              setNewFolderName('');
            }}
            style={{
              marginTop: '10px',
              background: 'none',
              border: 'none',
              color: c.ink3,
              fontSize: '12px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Folder baru
          </button>
        </>
      ) : (
        <>
          <div
            style={{
              fontSize: '12.5px',
              color: c.ink3,
              lineHeight: 1.55,
              maxWidth: '230px',
              marginTop: '8px',
            }}
          >
            Upload PDF, Word, Excel, atau gambar. Centang file supaya Power AI membacanya di chat ini.
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onMouseEnter={() => setPickHover(true)}
            onMouseLeave={() => setPickHover(false)}
            style={{
              marginTop: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              height: '34px',
              padding: '0 14px',
              borderRadius: '9px',
              background: c.panel,
              border: `1px solid ${pickHover ? c.line2 : c.line}`,
              color: c.ink,
              fontSize: '12.5px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'border-color 0.15s ease',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v12" />
              <path d="M17 8l-5-5-5 5" />
              <path d="M5 21h14" />
            </svg>
            Pilih file
          </button>
        </>
      )}
    </div>
  );

  // Collapsed edge tab (overlay mode): out of the way, one tap to expand.
  if (overlay && overlayCollapsed) {
    return (
      <button
        type="button"
        onClick={() => setOverlayCollapsed(false)}
        title="Buka panel"
        aria-label="Buka panel"
        className="pa-focusable"
        style={{
          position: 'fixed',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
          padding: '12px 8px',
          border: `1px solid ${c.line2}`,
          borderRight: 'none',
          borderRadius: '12px 0 0 12px',
          background: theme === 'dark' ? '#101215' : '#FFFFFF',
          color: inspect.streaming ? c.jade : c.ink2,
          cursor: 'pointer',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.22)',
        }}
      >
        {inspect.streaming ? (
          <span style={{
            width: '11px', height: '11px', borderRadius: '50%',
            border: `2px solid ${c.line2}`, borderTopColor: c.jade,
            display: 'inline-block', animation: 'spin 0.7s linear infinite',
          }} />
        ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
        )}
        {files.length > 0 && (
          <span style={{ fontFamily: c.mono, fontSize: '10px', color: c.ink3 }}>
            {files.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: overlay ? 'min(320px, 92vw)' : `${RIGHT_PANEL_WIDTH}px`,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        // Overlay mode floats above thread text, so it needs a solid surface;
        // docked mode keeps the transparent-over-app-background look.
        backgroundColor: overlay
          ? (theme === 'dark' ? '#101215' : '#FFFFFF')
          : 'transparent',
        borderLeft: `1px solid ${c.line}`,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        boxShadow: dragOver
          ? `inset 0 0 0 1.5px ${c.jade}`
          : overlay
            ? '-14px 0 36px rgba(0,0,0,0.35)'
            : 'none',
        transition: 'box-shadow 0.15s ease',
        zIndex: 50,
      }}
    >
      {/* Overlay mode: a notch on the left edge collapses the panel back into
          the slim tab, so it never has to keep covering the thread. */}
      {overlay && (
        <button
          type="button"
          onClick={() => setOverlayCollapsed(true)}
          title="Ciutkan panel"
          aria-label="Ciutkan panel"
          className="pa-focusable"
          style={{
            position: 'absolute',
            left: '-22px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '22px',
            height: '52px',
            display: 'grid',
            placeItems: 'center',
            border: `1px solid ${c.line2}`,
            borderRight: 'none',
            borderRadius: '10px 0 0 10px',
            background: theme === 'dark' ? '#101215' : '#FFFFFF',
            color: c.ink3,
            cursor: 'pointer',
            boxShadow: '-6px 0 18px rgba(0,0,0,0.18)',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      )}
      {/* Single hidden picker, driven by the empty-state "Pilih file" button.
          Upload is allowed with or without an active conversation. */}
      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handlePick}
      />

      {/* Header, title + jade count badge. Height matches the desktop navbar
          (50px) so the bottom hairline reads as one continuous line. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          height: '50px',
          minHeight: '50px',
          padding: '0 20px',
          borderBottom: `1px solid ${c.line}`,
          flexShrink: 0,
        }}
      >
        {inspect.streaming && (
          <span style={{
            width: '11px', height: '11px', borderRadius: '50%',
            border: `2px solid ${c.line2}`, borderTopColor: c.jade,
            display: 'inline-block', animation: 'spin 0.7s linear infinite', flexShrink: 0,
          }} />
        )}
        <span style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '-0.01em', color: c.ink }}>
          {inspect.streaming ? 'Lagi kerja' : 'File'}
        </span>
        {/* Badge counts what Power AI will read: selected library files plus
            always-on project files. */}
        {activeCount > 0 && (
          <span
            style={{
              fontSize: '11px',
              fontWeight: 600,
              lineHeight: 1,
              color: c.jade,
              backgroundColor: c.jadeDim,
              borderRadius: '999px',
              padding: '3px 8px',
              fontFamily: c.mono,
            }}
          >
            {activeCount}
          </span>
        )}
        <button
          type="button"
          onClick={() => openDesk()}
          title="Buka layar penuh"
          aria-label="Buka file layar penuh"
          style={{
            marginLeft: 'auto',
            width: '28px',
            height: '28px',
            display: 'grid',
            placeItems: 'center',
            borderRadius: '8px',
            border: `1px solid ${c.line}`,
            background: c.panel,
            color: c.ink2,
            cursor: 'pointer',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3" />
            <path d="M16 3h3a2 2 0 0 1 2 2v3" />
            <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
            <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
          </svg>
        </button>
      </div>
      {libraryMeta.personal_max_bytes > 0 && (
        <div style={{ padding: '8px 20px 10px', borderBottom: `1px solid ${c.line}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: c.ink3, marginBottom: '5px' }}>
            <span>Penyimpanan</span>
            <span style={{ fontFamily: c.mono }}>
              {prettySize(libraryMeta.personal_used_bytes)} / {prettySize(libraryMeta.personal_max_bytes)}
            </span>
          </div>
          <div style={{ height: '3px', borderRadius: '99px', background: c.line, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${Math.min(100, (libraryMeta.personal_used_bytes / libraryMeta.personal_max_bytes) * 100)}%`,
                background:
                  libraryMeta.personal_used_bytes / libraryMeta.personal_max_bytes > 0.9
                    ? ERROR_RED
                    : c.jade,
              }}
            />
          </div>
        </div>
      )}

      {/* Scrollable content */}
      <div
        className="custom-scrollbar"
        style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
      >
        {error && (
          <div style={{ margin: '14px 16px 0', fontSize: '12px', color: ERROR_RED, lineHeight: 1.4 }}>
            {error}
          </div>
        )}

        {hasItems && !deskOpen ? (
          <div style={{ padding: '10px 12px 24px', display: 'flex', flexDirection: 'column', gap: hasRun ? '2px' : '20px' }}>
            <div style={{ padding: inspect.steps.length > 0 ? '4px 4px 10px' : 0 }}>
            <InspectorProgress
              c={c}
              steps={inspect.steps}
              streaming={inspect.streaming}
              durationMs={inspect.durationMs}
            />
            </div>
            <InspectorHasil c={c} decks={decks} images={images} />
            <InspectorSources
              c={c}
              sources={contextSources}
              onFileClick={(name) => {
                const target = [...files, ...projectFiles].find((file) => file.filename === name);
                openDesk(target);
              }}
            />
            {hasRun ? (
              (files.length + projectFiles.length + uploading.length + libraryMeta.folders.length + projectMeta.folders.length > 0
                || newFolderFor != null) && (
                <div>
                  <AccordionRow
                    c={c}
                    icon={<FileGlyph color="currentColor" />}
                    label="File"
                    count={files.length + projectFiles.length}
                    open={filePeekOpen}
                    onToggle={() => setFilePeekOpen((value) => !value)}
                  />
                  {filePeekOpen && (
                    <div style={{ padding: '6px 4px 8px' }}>
                      <button
                        type="button"
                        onClick={() => openDesk()}
                        style={{
                          background: 'none',
                          border: 0,
                          padding: '0 4px 10px',
                          fontSize: '11px',
                          fontWeight: 500,
                          color: c.jade,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        Buka workspace
                      </button>
                      {fileSection}
                    </div>
                  )}
                </div>
              )
            ) : (
              fileSection
            )}
          </div>
        ) : deskOpen ? null : (
          emptyState
        )}
      </div>
      {deskOpen && (
        <FileWorkspaceDesk
          theme={theme}
          themeColors={themeColors}
          onClose={closeDesk}
          usedBytes={libraryMeta.personal_used_bytes}
          maxBytes={libraryMeta.personal_max_bytes}
          error={error}
          previewFile={previewFile}
          onDownload={handleDownload}
          dragOver={dragOver}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {hasItems ? (
            <>
              {fileSection}
              {hasilSection}
            </>
          ) : (
            emptyState
          )}
        </FileWorkspaceDesk>
      )}
    </div>
  );
};
